import { yupResolver } from '@hookform/resolvers/yup';
import { AxiosError } from 'axios';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useMutation } from 'react-query';
import { toast } from 'react-toastify';
import { addNewUser, fetchDepartmentsPaginated } from 'services/registration';
import { APIErrors } from 'types/api';
import { AddNewUserValue } from '.';
import { schema } from './validation';
import { Workplace } from 'views/employee-management/useEmployees';

const defaultValues = {
    first_name: '',
    last_name: '',
    email: '',
    phone: '000000000',
    employee_position_name: '',
    workplace_id: '',
    department_id: '',
};

export const useAddNewUserForm = ({ adminWorkplaces, onCancel, selectedWorkplaceId }: { adminWorkplaces: Workplace[]; onCancel: (boolean) => void; selectedWorkplaceId?: number }) => {
    const { t } = useTranslation();

    const form = useForm<AddNewUserValue>({
        resolver: yupResolver(schema),
        defaultValues,
    });

    const { setValue, watch, setError } = form;

    const workplaceId = Number(watch('workplace_id'));

    const [selectedDepartment, setSelectedDepartment] = useState<{ label: string; value: number } | null>(null);

    useEffect(() => {
        if (selectedWorkplaceId) {
            setValue('workplace_id', String(selectedWorkplaceId));
        } else if (adminWorkplaces.length > 0) {
            setValue('workplace_id', String(adminWorkplaces[0].id));
        }
    }, [adminWorkplaces, selectedWorkplaceId, setValue]);

    useEffect(() => {
        setSelectedDepartment(null);
        setValue('department_id', '');
    }, [workplaceId, setValue]);

    const loadDepartmentOptions = async (search: string, _: any, { page }: { page: number }) => {
        const res = await fetchDepartmentsPaginated(workplaceId, page);
        return {
            options: res.data.map((d: any) => ({ label: d.name, value: d.id })),
            hasMore: res.current_page < res.last_page,
            additional: { page: page + 1 },
        };
    };

    const handleDepartmentChange = (
        option: { label: string; value: number } | null,
        fieldOnChange: (val: number | string | null) => void
    ) => {
        setSelectedDepartment(option);
        fieldOnChange(option?.value ?? '');
    };

    const mutation = useMutation(addNewUser, {
        onSuccess: () => {
            toast.success(t('employeeManagement.created'));
            onCancel(true);
        },
        onError: (error: AxiosError<APIErrors>) => {
            const errors = error.response?.data?.errors;
            if (errors) {
                Object.entries(errors).forEach(([key, value]) => {
                    setError(key as keyof AddNewUserValue, {
                        message: value?.[0],
                    });
                });
            }
        },
    });

    const onSubmit = (data: AddNewUserValue) => {
        mutation.mutate({ data });
    };

    return {
        t,
        form,
        workplaceId,
        selectedDepartment,
        loadDepartmentOptions,
        handleDepartmentChange,
        onSubmit,
        isSubmitting: mutation.isLoading,
    };
};